import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../Authentication/AuthContext";
import Login from "../../Authentication/Login/Login";
import "./BookViewPage.css";

type BookLevelKey = "A1" | "A2" | "B1" | "B2" | "C1" | "C2";
type BookViewStatus = "checking" | "loading" | "active" | "inactive" | "error";

const API_BASE_URL = "https://www.api.dearlanguage.com";

const ALL_BOOK_LEVELS: BookLevelKey[] = ["A1", "A2", "B1", "B2", "C1", "C2"];

const normalizeBookLevel = (value: string | null): BookLevelKey => {
  const cleanValue = (value || "A1").trim().toUpperCase();

  return ALL_BOOK_LEVELS.includes(cleanValue as BookLevelKey)
    ? (cleanValue as BookLevelKey)
    : "A1";
};

const getValidBookId = (value: string | null, fallbackFile: string): number => {
  const directValue = Number(value || 0);

  if (
    Number.isInteger(directValue) &&
    directValue >= 1 &&
    directValue <= 10
  ) {
    return directValue;
  }

  const fileMatch = fallbackFile.match(/_([0-9]+)\.html$/i);
  const fileBookId = Number(fileMatch?.[1] || 0);

  if (
    Number.isInteger(fileBookId) &&
    fileBookId >= 1 &&
    fileBookId <= 10
  ) {
    return fileBookId;
  }

  return 1;
};

const getCleanApiBaseUrl = (): string => {
  return API_BASE_URL.replace(/\/+$/, "");
};

const BookViewPage: React.FC = () => {
  const navigate = useNavigate();
  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  const { isLoggedIn, authLoading, userEmail } = useAuth();

  const [showLoginModal, setShowLoginModal] = useState<boolean>(false);
  const [status, setStatus] = useState<BookViewStatus>("checking");
  const [bookHtml, setBookHtml] = useState<string>("");
  const [errorText, setErrorText] = useState<string>("");
  const [retryKey, setRetryKey] = useState<number>(0);
  const [isBookLoaded, setIsBookLoaded] = useState<boolean>(false);

  const params = useMemo(() => new URLSearchParams(window.location.search), []);

  const level = normalizeBookLevel(params.get("level"));
  const fallbackFile = params.get("file") || `${level}_1.html`;
  const bookId = getValidBookId(params.get("bookId"), fallbackFile);
  const bookTitle = params.get("book") || `Deutsch ${level}.${bookId}`;

  const cleanEmail = userEmail?.trim().toLowerCase() || "";

  const bookUrl = useMemo(() => {
    const searchParams = new URLSearchParams({
      email: cleanEmail,
    });

    return `${getCleanApiBaseUrl()}/api/protected-books/${encodeURIComponent(
      level,
    )}/${encodeURIComponent(String(bookId))}?${searchParams.toString()}`;
  }, [level, bookId, cleanEmail]);

  useEffect(() => {
    document.documentElement.classList.add("book-view-screen-page");
    document.body.classList.add("book-view-screen-page");

    return () => {
      document.documentElement.classList.remove("book-view-screen-page");
      document.body.classList.remove("book-view-screen-page");
    };
  }, []);

  useEffect(() => {
    const existingPreconnect = document.querySelector(
      'link[data-dear-language-api-preconnect="true"]',
    );

    if (existingPreconnect) {
      return;
    }

    const preconnect = document.createElement("link");
    preconnect.rel = "preconnect";
    preconnect.href = getCleanApiBaseUrl();
    preconnect.setAttribute("data-dear-language-api-preconnect", "true");

    document.head.appendChild(preconnect);
  }, []);

  useEffect(() => {
    setIsBookLoaded(false);
  }, [bookUrl, status]);

  useEffect(() => {
    let isCancelled = false;

    const loadBook = async (): Promise<void> => {
      setErrorText("");
      setBookHtml("");

      if (authLoading) {
        setStatus("checking");
        return;
      }

      if (!isLoggedIn) {
        setStatus("inactive");
        return;
      }

      if (!cleanEmail) {
        setStatus("error");
        setErrorText(
          "Ihre E-Mail-Adresse konnte nicht gelesen werden. Bitte loggen Sie sich erneut ein.",
        );
        return;
      }

      setStatus("loading");

      try {
        const response = await fetch(bookUrl, {
          method: "GET",
          cache: "no-store",
          headers: {
            Accept: "text/html,application/json",
          },
        });

        if (isCancelled) {
          return;
        }

        if (response.status === 401 || response.status === 403) {
          setStatus("inactive");
          return;
        }

        if (!response.ok) {
          setStatus("error");
          setErrorText(
            `Das Buch konnte nicht geladen werden. Status: ${response.status}`,
          );
          return;
        }

        const html = await response.text();

        if (!isCancelled) {
          setBookHtml(html);
          setStatus("active");
        }
      } catch (error) {
        if (!isCancelled) {
          setStatus("error");
          setErrorText(
            error instanceof Error
              ? error.message
              : "Das Buch konnte nicht geladen werden.",
          );
        }
      }
    };

    void loadBook();

    return () => {
      isCancelled = true;
    };
  }, [authLoading, isLoggedIn, cleanEmail, bookUrl, retryKey]);

  const scrollInsideBook = (hash: string): void => {
    const iframeDocument = iframeRef.current?.contentDocument;

    if (!iframeDocument || !hash) {
      return;
    }

    const cleanHash = hash.replace("#", "");
    const target =
      iframeDocument.getElementById(cleanHash) ||
      iframeDocument.querySelector(`[name="${cleanHash}"]`);

    if (target) {
      target.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  const setupIframeControls = (): void => {
    const iframe = iframeRef.current;
    const iframeWindow = iframe?.contentWindow;
    const iframeDocument = iframe?.contentDocument;

    if (!iframeWindow || !iframeDocument) {
      return;
    }

    iframeDocument.onclick = (event: MouseEvent) => {
      const target = event.target as HTMLElement | null;

      if (!target) {
        return;
      }

      const link = target.closest("a") as HTMLAnchorElement | null;

      if (!link) {
        return;
      }

      const hrefValue = link.getAttribute("href");

      if (!hrefValue) {
        return;
      }

      if (hrefValue.startsWith("#")) {
        event.preventDefault();
        event.stopPropagation();
        scrollInsideBook(hrefValue);
        return;
      }

      try {
        const parsedUrl = new URL(hrefValue, iframeWindow.location.href);

        if (parsedUrl.hash) {
          event.preventDefault();
          event.stopPropagation();
          scrollInsideBook(parsedUrl.hash);
        }
      } catch {
        return;
      }
    };
  };

  const handleIframeLoad = (): void => {
    setIsBookLoaded(true);

    window.setTimeout(() => {
      setupIframeControls();
    }, 0);
  };

  const handleLoginClose = (): void => {
    setShowLoginModal(false);
  };

  const renderLoadingIndicator = (
    extraClassName: string = "",
  ): React.ReactNode => {
    return (
      <div
        className={`book-view-loading-overlay ${extraClassName}`}
        role="status"
        aria-live="polite"
      >
        <div className="book-view-loading-spinner" aria-hidden="true" />

        <div className="book-view-loading-text">
          <strong>Buch wird geladen</strong>
          <span>Bitte warten Sie, bis das Buch vollständig geladen ist.</span>
        </div>
      </div>
    );
  };

  const renderLockedCard = (): React.ReactNode => {
    const isChecking = status === "checking" || authLoading;
    const isLoading = status === "loading";
    const isError = status === "error";

    if (isChecking || isLoading) {
      return renderLoadingIndicator("book-view-loading-panel");
    }

    let badgeText = "Buchzugang";
    let title = "Dieses Buch ist nicht freigeschaltet";
    let text =
      "Bitte kaufen Sie den passenden Kurs oder prüfen Sie Ihren Zugang erneut.";

    if (!isLoggedIn) {
      badgeText = "Login erforderlich";
      title = "Bitte einloggen";
      text =
        "Bitte loggen Sie sich ein. Danach prüfen wir automatisch, ob dieses Buch für Ihr Konto freigeschaltet ist.";
    }

    if (isError) {
      badgeText = "Fehler";
      title = "Buch konnte nicht geöffnet werden";
      text = errorText || "Der Bücherzugang konnte nicht geprüft werden.";
    }

    return (
      <div className="book-view-center">
        <div className="book-lock-card">
          <p className="book-lock-badge">{badgeText}</p>

          <h2>{title}</h2>

          <p>{text}</p>

          {!isLoggedIn && (
            <button
              type="button"
              className="book-lock-button"
              onClick={() => setShowLoginModal(true)}
            >
              Einloggen
            </button>
          )}

          {isLoggedIn && status === "inactive" && (
            <button
              type="button"
              className="book-lock-button"
              onClick={() => navigate("/courses")}
            >
              Kurs kaufen
            </button>
          )}

          {isError && (
            <button
              type="button"
              className="book-lock-button"
              onClick={() => setRetryKey((current) => current + 1)}
            >
              Zugang erneut prüfen
            </button>
          )}

          <button
            type="button"
            className="book-lock-button"
            onClick={() => navigate("/books")}
          >
            Zurück zu Büchern
          </button>
        </div>
      </div>
    );
  };

  const shouldShowBook =
    !authLoading && isLoggedIn && status === "active" && Boolean(bookHtml);

  if (authLoading || status === "checking") {
    return (
      <div className="book-view-page">
        <Login isOpen={showLoginModal} onClose={handleLoginClose} />
        {renderLoadingIndicator("book-view-loading-page")}
      </div>
    );
  }

  return (
    <div className="book-view-page">
      <Login isOpen={showLoginModal} onClose={handleLoginClose} />

      <section className="book-view-layout">
        <div className="book-view-topbar">
          <div className="book-view-title-block">
            <p className="book-view-kicker">{level} Deutschbuch</p>
            <h1 className="book-view-title">{bookTitle}</h1>
          </div>

          <div className="book-view-actions">
            <button
              type="button"
              className="book-view-back"
              onClick={() => navigate("/books")}
            >
              Zurück zu Büchern
            </button>

            {!shouldShowBook && (
              <button
                type="button"
                className="book-view-secondary"
                onClick={() => navigate("/courses")}
              >
                Kurse
              </button>
            )}
          </div>
        </div>

        {shouldShowBook ? (
          <div className="book-frame-panel">
            <iframe
              ref={iframeRef}
              title={bookTitle}
              className={`book-frame ${
                !isBookLoaded ? "book-frame-loading" : ""
              }`}
              srcDoc={bookHtml}
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              referrerPolicy="no-referrer"
              onLoad={handleIframeLoad}
            />

            {!isBookLoaded &&
              renderLoadingIndicator("book-view-loading-panel")}
          </div>
        ) : (
          renderLockedCard()
        )}
      </section>
    </div>
  );
};

export default BookViewPage;